import React, {useEffect, useState} from "react";
import {Button, Checkbox, Label} from "flowbite-react";
import {VisualOptionsGraphic_SurveyQuestionPage_Options} from "data/models/survey/visual-options-graphic";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {RenderableTitle} from "data/models/survey/base";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {VisualOptionsGraphicRequest, VisualOptionsGraphicResponse} from "data/models/shared/visual-options-graphic";
import {httpsCallable} from "firebase/functions";
import {functions} from "hooks/firebase";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/free-solid-svg-icons";
import {VisualOptionsGraphic} from "data/models/shared/bc-sheet/visual-options-graphic";
import Loading from "components/Loading";
import {assertNoError} from "helpers/asserts";

export type MultiChoiceSelectVisual_SurveyQuestionPage_Props = {
  options: VisualOptionsGraphic_SurveyQuestionPage_Options,
  title: RenderableTitle,
  description: RenderableTitle | null,
  questionId: string,
  value: string[] | null | undefined,
  onChange: (id: string, value: string[] | null) => void,
}

const CHOICES_LIMIT = 12;

const visualOptionsGraphicFunction = httpsCallable<VisualOptionsGraphicRequest, VisualOptionsGraphicResponse>(functions, 'visualOptionsGraphic');

export default function VisualOptionsGraphicSurveyQuestionPage(
    {
      title,
      description,
      questionId,
      value,
      onChange
    }: MultiChoiceSelectVisual_SurveyQuestionPage_Props
) {
  const [choicesOffset, setChoicesOffset] = useState(0);
  const [choices, setChoices] = useState<VisualOptionsGraphic[]>([]);
  const [fetchChoices, setFetchChoices] = useState<boolean>(true);
  const [loadingChoices, setLoadingChoices] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalChoicesCount, setTotalChoicesCount] = useState<number>(999);

  useEffect(() => {
    if (loadingChoices || !fetchChoices) {
      return
    }
    const visualOptionsGraphicRequest = {
      offset: choicesOffset,
      limit: CHOICES_LIMIT,
    };
    setFetchChoices(false);
    setLoadingChoices(true);
    setChoicesOffset(Math.min(choicesOffset + CHOICES_LIMIT, totalChoicesCount));

    visualOptionsGraphicFunction(visualOptionsGraphicRequest).then(result => {
      setChoices([...choices, ...result.data.graphics]);
      setTotalChoicesCount(result.data.totalCount);
      setLoadingChoices(false);
      setError(null);
    }).catch((error) => {
      setError(error as Error);
      setLoadingChoices(false);
    });
  }, [loadingChoices, choices, fetchChoices]);

  function handleChange(changedValue: string) {
    if (isNullOrUndefined(value)) {
      const newValues = [changedValue];
      onChange(questionId, newValues);
    } else if (value.includes(changedValue)) {
      const newValues = value.filter(v => v !== changedValue);
      onChange(questionId, newValues.length !== 0 ? newValues : null);
    } else {
      const newValues = [...value, changedValue];
      onChange(questionId, newValues);
    }
  }

  assertNoError(error);

  const showMoreVisible = totalChoicesCount !== null && totalChoicesCount > choices.length;

  function handleShowMore() {
    setFetchChoices(true);
  }

  return <div className={"flex flex-col w-full"}>
    <div className={QUESTION_CONTAINER_CLASS}>
      <fieldset name={questionId} className="" autoFocus={true}>
        <div className={"my-4 text-center"}>{title}</div>
        {description !== null && <div className={"my-4 text-center"}>{description}</div>}
        <div className={"p-4 bg-neutral-50 flex flex-wrap gap-4 justify-center rounded-xl"}>
          {choices.map((choice, index) => <div key={index} className="flex flex-col items-center gap-2">
            <Checkbox
                className="hidden peer"
                id={`${questionId}.${choice.visualOption}`}
                value={choice.visualOption}
                checked={value?.includes(choice.visualOption) ?? false}
                onChange={({target: {value: changedValue}}) => handleChange(changedValue)}
            />
            <Label
                htmlFor={`${questionId}.${choice.visualOption}`}
                className={"inline-flex p-2 bg-white border-3 border-neutral-50 rounded-lg cursor-pointer peer-checked:border-bc-blue peer-checked:text-bc-blue hover:text-neutral-600 hover:bg-neutral-100"}
            >
              <div className={"bg-white p-2 rounded-lg"}>
                <div className={"flex flex-col justify-center w-40 h-40 md:w-48 md:h-48"}>
                  <img src={choice.imageUrl} alt={choice.visualOption}/>
                </div>
              </div>
            </Label>
          </div>)}
        </div>
        {loadingChoices && <Loading className={"mt-12"} loadingText={"Loading Images..."}/>}
        {showMoreVisible && <div className={"flex flex-row justify-center my-12"}>
            <Button
                className={"px-12 rounded-lg"}
                color={"gray"}
                onClick={() => handleShowMore()}
                disabled={fetchChoices || loadingChoices}
            >
                <div className={"flex flex-col"}>
                    <div><span className={"text-neutral-500 font-bold"}>Show more</span></div>
                    <div><FontAwesomeIcon icon={faChevronDown} size={"2xl"}/></div>
                </div>
            </Button>
        </div>}
      </fieldset>
    </div>
  </div>
}
